import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { stringify } from "qs";
import { css } from "@emotion/react";
import { decamelize, decamelizeKeys } from "humps";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CabinClassBlock } from "main/javascripts/components/flight/form/CabinClassBlock";
import { FlightOriginDestinationDate } from "main/javascripts/components/flight/FlightOriginDestinationDate";
import { ButtonWithLoader } from "main/javascripts/components/button/ButtonWithLoader";
import { GuestsBlock } from "main/javascripts/components/hotel/GuestsBlock";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import { debouncedFetchFlightDestinationSuggestions } from "../../flight/flightSuggestionSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  isDesktop,
  moreThanBreakpoint,
} from "main/javascripts/styles/base/responsiveStyle";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";
import {
  convertSearchDynamicPackageFormValuesToParams,
  postSearchDynamicPackageCache,
} from "../../dynamicPackage/searchDynamicPackageSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { dpAvailabilitiesEvent } from "main/javascripts/utils/googleTagManagerUtil";
import { postSelector } from "../postSlice";
import { stopBodyScrolling } from "main/javascripts/utils/ScrollUtil";
import {
  fontSize,
  letterSpacing,
} from "main/javascripts/styles/base/typographyStyle";
import { borderColor } from "main/javascripts/styles/base/colorStyle";
import { SearchDynamicPackageFieldButton } from "./SearchDynamicPackageFieldButton";
import { ModalDialogWithClose } from "main/javascripts/components/atoms/Modal/ModalDialogWithClose";
import { FullScreenModalDialog } from "main/javascripts/components/molecules/Modal/FullScreenModalDialog";
import { PostSearchGAParams } from "main/javascripts/constants/PostConstants";
import { TABI_DOMAIN } from "main/javascripts/constants/Constants";
import {
  FormNamespaces,
  yupDefaultMessages,
} from "main/javascripts/constants/FormConstants";
import { FlightTypes } from "main/javascripts/constants/FlightTypes";
import { convertServerErrorToForm } from "main/javascripts/utils/FormUtil";
import { FormErrorsBlock } from "main/javascripts/components/form/FormErrorsBlock";

const defaultValues = {
  departure_date: null, // undefinedではダメ
  return_date: null, // undefinedではダメ
  traveler: {
    number_of_adult: 2,
    number_of_children: 0,
  },
  access_type: "direct_web",
  flight_type: decamelize("roundTrip"),
  cabin_class: "Y",
  itineraries: [{ departure_date: null }, { departure_date: null }],
  number_of_rooms: 1,
  guests: [
    {
      number_of_adult: 2,
      number_of_children: 0,
      ages_of_children: [],
    },
  ],
};

type SearchDynamicPackageFormData = {
  form?: any; // エラー用
  flight_type: string;
  origin: string;
  origin_id: number;
  origin_type: string;
  destination: string;
  destination_id: number;
  destination_type: string;
  departure_date: string;
  return_date: string;
  itineraries: any[];
  guests: any[];
  number_of_rooms: number;
};

yup.setLocale(yupDefaultMessages);
const schema = yup.object().shape({
  origin: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.string().nullable().required(),
    }),
  origin_id: yup
    .number()
    .nullable()
    .transform((_, val) => (val ? Number(val) : null))
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.number().nullable().required(),
    }),
  destination: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.string().nullable().required(),
    }),
  destination_id: yup
    .number()
    .nullable()
    .transform((_, val) => (val ? Number(val) : null))
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.number().nullable().required(),
    }),
  departure_date: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.string().nullable().required(),
    }),
  return_date: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val === FlightTypes.roundTrip,
      then: yup.string().nullable().required(),
    }),
  itineraries: yup
    .array()
    .nullable()
    .when("flight_type", {
      is: (val) => val === FlightTypes.multiCity,
      then: (schema) =>
        schema.of(
          yup.object().shape({
            origin: yup.string().required(),
            origin_id: yup.number().required(),
            destination: yup.string().required(),
            destination_id: yup.number().required(),
            departure_date: yup.string().nullable().required(),
          })
        ),
    }),
  guests: yup.array().of(
    yup.object().shape({
      number_of_children: yup
        .number()
        .nullable()
        .transform((_, val) => (val || val === 0 ? Number(val) : null))
        .required(),
      ages_of_children: yup.array().when("number_of_children", {
        is: (val) => Number(val) > 0,
        then: yup.array().of(
          yup.object().shape({
            age: yup
              .number()
              .nullable()
              .transform((_, val) => (val || val === 0 ? Number(val) : null))
              .required(),
          })
        ),
      }),
    })
  ),
});

export interface IProps {
  isCompact?: boolean;
}

export const SearchDynamicPackage: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { isCompact = false } = props;
  const appDispatch = useAppDispatch();

  const { t } = useTranslation(["component"]);

  const post = useAppSelector(postSelector);

  // TODO: 旧reducer
  const breakpoint = useSelector<any>(
    (state) => state.breakpointReducer.breakpoint
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayedPicker, setDisplayedPicker] = useState(false);

  let defaultOrigin = {};
  if (post.origins?.length > 0) {
    const origin = post.origins[0];
    defaultOrigin = {
      origin: origin.longName,
      origin_id: origin.id,
      origin_type: origin.destinationType,
    };
  }
  let defaultDestination = {};
  if (post.flightDestinations?.length > 0) {
    const destination = post.flightDestinations[0];
    defaultDestination = {
      destination: destination.name,
      destination_id: destination.id,
      destination_type: destination.destinationType,
    };
  }
  const defaultOriginDestination = {
    ...defaultOrigin,
    ...defaultDestination,
  };

  useEffect(() => {
    if (isDesktop(breakpoint)) {
      stopBodyScrolling(displayedPicker);
    }
  }, [displayedPicker]);

  const methods = useForm<SearchDynamicPackageFormData>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      ...defaultOriginDestination,
    },
  });
  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    clearErrors("form");
    handleSubmit(onSubmit)(e);
  };

  const onSubmit: (values: any, form: any) => void = async (
    values: any,
    _: any
  ) => {
    setIsSubmitting(true);
    const params: any = convertSearchDynamicPackageFormValuesToParams(values);
    return appDispatch(
      postSearchDynamicPackageCache({
        params: { ...params, ...PostSearchGAParams },
      })
    )
      .then(unwrapResult)
      .then((result) => {
        dpAvailabilitiesEvent(result.data.dynamicPackageId, params);
        const gaParams = stringify(PostSearchGAParams, {
          arrayFormat: "indices",
        });
        window.location.href = `${TABI_DOMAIN}/flights?${stringify(
          decamelizeKeys(result.data.conditions),
          { arrayFormat: "indices" }
        )}
&cache_id=${result.data.cacheId}
&dynamic_package_id=${result.data.dynamicPackageId}&search_group_id=${
          result.data.searchGroupId
        }&search_type=dp&${gaParams}`;
      })
      .catch((errorResponse) => {
        setIsSubmitting(false);
        if (Array.isArray(errorResponse.errors)) {
          const errorMessages = errorResponse.errorMessages;
          convertServerErrorToForm({ messages: errorMessages }, setError);
        } else {
          // エラー形式を合わせるために変換している
          const errors = Object.keys(errorResponse.errors).map((key) => {
            return { [key]: errorResponse.errors[key] };
          });
          convertServerErrorToForm({ fieldMessages: errors }, setError);
        }
      });
  };

  const fetchFlightDestinationSuggestionsAction = (data: any, type: string) => {
    appDispatch(
      debouncedFetchFlightDestinationSuggestions({
        params: { text: data.text },
        type,
      })
    );
  };

  const hidePicker: () => void = () => {
    setDisplayedPicker(false);
  };

  const onPickerButtonClick: () => void = () => {
    setDisplayedPicker(true);
  };

  const formFieldsStyles = isCompact
    ? [formFieldsStyle, formScrollStyle]
    : formFieldsStyle;

  const renderForm = () => {
    return (
      <>
        {isCompact && (
          <div css={headerTitleStyle}>{t("post.dynamicPackageSearch")}</div>
        )}
        <form
          method="GET"
          action="/flights"
          onSubmit={handleFormSubmit}
          css={formStyle}
        >
          <div css={formInnerStyle}>
            <div css={formFieldsStyles}>
              <FlightOriginDestinationDate
                destinationSuggestOptions={{}}
                fetchFlightOriginDestinationSuggestions={
                  fetchFlightDestinationSuggestionsAction
                }
                defaultOrigins={post.origins}
                defaultDestinations={post.flightDestinations}
                useDatePicker={true}
                labelColorStyleKey="default"
                borderColorStyleKey="primary"
                disableStopBodyScrolling
              />
              <div css={flightClassBlockStyle}>
                <CabinClassBlock
                  labelColorStyleKey="default"
                  borderColorStyleKey="primary"
                />
              </div>
              <GuestsBlock
                labelColorStyleKey="default"
                borderColorStyleKey="primary"
              />
              {errors && (
                <div>
                  <FormErrorsBlock
                    namespace={FormNamespaces.flight}
                    errors={errors}
                    hideValidationErrors={true}
                  />
                </div>
              )}
            </div>
            <div css={submitBlockStyle}>
              <div css={buttonBlockStyle}>
                <ButtonWithLoader
                  type="submit"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  検 索
                </ButtonWithLoader>
              </div>
            </div>
          </div>
        </form>
      </>
    );
  };

  return (
    <div css={blockStyle}>
      <FormProvider {...methods}>
        {isCompact ? (
          <>
            <SearchDynamicPackageFieldButton onClick={onPickerButtonClick} />
            {isDesktop(breakpoint) ? (
              <ModalDialogWithClose
                displayed={displayedPicker}
                closeHandler={hidePicker}
                styleKey="noInnerContentPadding"
                innerStyles={{
                  maxWidth: maxWidth.page1col,
                  height: "680px",
                }}
              >
                {renderForm()}
              </ModalDialogWithClose>
            ) : (
              <FullScreenModalDialog
                displayed={displayedPicker}
                closeHandler={hidePicker}
                colorMode="light"
              >
                {renderForm()}
              </FullScreenModalDialog>
            )}
          </>
        ) : (
          renderForm()
        )}
      </FormProvider>
    </div>
  );
};

const blockStyle = css`
  margin: 0 auto;
`;
const headerTitleStyle = css`
  position: absolute;
  top: 0;
  left: 50%;
  line-height: 3rem;
  font-size: ${fontSize.mediumHeading};
  font-weight: bold;
  letter-spacing: ${letterSpacing.text};
  transform: translateX(-50%);
`;
const formStyle = css`
  height: 100%;
  border-top: 1px solid ${borderColor.secondaryLightColor};
  box-sizing: border-box;
`;
const formInnerStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const formFieldsStyle = css`
  height: 100%;
  padding: ${space.atom};

  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom} ${space.atom2x};
  }
`;
const formScrollStyle = css`
  overflow-y: auto;
`;
const flightClassBlockStyle = css`
  width: 50%;
  min-width: 200px;
`;
const submitBlockStyle = css`
  padding: ${space.atom};
  border-top: 1px solid ${borderColor.secondaryLightColor};

  ${moreThanBreakpoint("tablet")} {
    display: flex;
    justify-content: flex-end;
    padding: ${space.atom} ${space.atom2x};
  }
`;
const buttonBlockStyle = css`
  ${moreThanBreakpoint("tablet")} {
    width: 50%;
  }
`;
